import { type QueryClient, type QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthProvider";
import { type AnalyticValues, useAnalytics } from "../contexts/analytics/hooks";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import type { PresetSettings, PrintoutPresetsResponse } from "../pages/DaySchedule/printoutPresets";
import { backendService } from "../services/backend.service";
import { isHeadNurse } from "../utils/utilFunctions";
import { useErrorSnackbar } from "./useErrorSnackbar";

/** Single source of truth for the presets query key, so the query and every mutation write the same entry. */
export const printoutPresetKeys = {
	department: (departmentId: string, userId: string) => ["printoutPresets", departmentId, userId] as const,
	/** Outside the per-user key on purpose: reloading a user's list must not refetch this support-only count. */
	defaultImpact: (departmentId: string) => ["printoutPresets", departmentId, "defaultImpact"] as const,
} as const;

const usePresetScope = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();
	const departmentId = chosenDepartment.department_id;
	return { departmentId, userInfo, queryKey: printoutPresetKeys.department(departmentId, userInfo.id) };
};

/**
 * The saved Detailed-printout presets this user sees in the chosen department: their own, or — while
 * they have no settings of their own — the department's, which they can still edit, rename and remove
 * (the server gives them their own copy on the first change). Loaded only for head nurses — the same
 * gate as `useUserSettings`, since only they open the printout settings.
 */
export const usePrintoutPresets = () => {
	const { departmentId, userInfo, queryKey } = usePresetScope();
	return useQuery<PrintoutPresetsResponse>({
		queryKey,
		queryFn: async () => (await backendService.getPrintoutPresets(departmentId)).data,
		enabled: !!departmentId && !!userInfo.id && isHeadNurse(userInfo.role),
		// Every mutation writes the server's full state back into the cache, so a refetch adds nothing.
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnWindowFocus: false,
	});
};

type PresetAction = "add" | "remove" | "rename" | "select" | "save";

/** Snapshot taken when a mutation starts, so a department switch mid-request can't write into the wrong cache. */
type PresetMutationContext = { queryKey: QueryKey; departmentId: string; previous: PrintoutPresetsResponse | undefined };

type PresetMutationConfig<TVariables> = {
	action: PresetAction;
	request: (departmentId: string, variables: TVariables) => Promise<AxiosResponse<PrintoutPresetsResponse>>;
	/** The preset the action is about, for analytics; `null` for Printout 1 or a preset not created yet. */
	presetIdOf: (variables: TVariables, state: PrintoutPresetsResponse | undefined) => string | null;
	/** Snackbar text when the server gave no specific reason. A `UIVisibleError` key replaces it. */
	failureMessage: (variables: TVariables) => string;
	/** False for autosave, which fires after every pause in editing — only its failures are worth an event. */
	trackLifecycle: boolean;
	/**
	 * The department the write belongs to, when it can outlive the department on screen: an autosave still
	 * waiting at a department switch is sent after the switch, and must go — and be cached — where it was made.
	 */
	// DEFAULT: absent = the department on screen when the write is made, which is where every other write was made.
	departmentOf?: (variables: TVariables) => string;
	/**
	 * What the server's answer changes in the cache. Every endpoint answers with the full state, but an
	 * answer only speaks with authority about what its own request changed: a save or rename answered
	 * after a later optimistic switch must not switch the user back. `laterSwitchPending`: a switch made
	 * after this write is still queued, and it — not this answer — owns which printout is in use.
	 */
	writeAnswer: (
		current: PrintoutPresetsResponse | undefined,
		answer: PrintoutPresetsResponse,
		variables: TVariables,
		laterSwitchPending: boolean,
	) => PrintoutPresetsResponse;
	/** Cache state to show before the server answers, and how to undo it if the request fails. */
	optimistic?: {
		apply: (current: PrintoutPresetsResponse, variables: TVariables) => PrintoutPresetsResponse;
		/** `current` back to `previous` as far as this request is concerned — never over a later request's change. */
		rollback: (
			current: PrintoutPresetsResponse,
			previous: PrintoutPresetsResponse,
			variables: TVariables,
			laterSwitchPending: boolean,
		) => PrintoutPresetsResponse;
	};
};

/**
 * The whole answer — for add, remove and select, which change the list or which printout is in use —
 * except that a switch the user made after this write keeps its choice until its own answer arrives.
 */
export const takeAnswerKeepingLaterSwitch = (
	current: PrintoutPresetsResponse | undefined,
	answer: PrintoutPresetsResponse,
	laterSwitchPending: boolean,
): PrintoutPresetsResponse => (current && laterSwitchPending ? { ...answer, activePresetId: current.activePresetId } : answer);

/**
 * Only `presetId`'s entry from the answer, in place — which printout is in use and every other entry stay
 * as the cache has them. Falls back to the whole answer when there is nothing to merge into (nothing
 * cached, or the cache or the answer no longer has the preset): then the server's state is the only truth.
 */
export const withAnsweredPreset = (
	current: PrintoutPresetsResponse | undefined,
	answer: PrintoutPresetsResponse,
	presetId: string,
): PrintoutPresetsResponse => {
	const answered = answer.presets.find((preset) => preset.id === presetId);
	// DEFAULT: nothing to merge into (nothing cached, or the cache or the answer no longer has the preset) —
	// the server's full state is then the only truth, so take all of it.
	if (!current || !answered || !current.presets.some((preset) => preset.id === presetId)) return answer;
	return { ...current, presets: current.presets.map((preset) => (preset.id === presetId ? answered : preset)) };
};

/**
 * Writes are queued per department: the server sees them in the order the user made them, and answers
 * come back in that order — so an older save can never land after a newer one.
 */
const presetWriteScope = (departmentId: string) => ({ id: `printout-preset-${departmentId}` });

/** Tags each write by action, so an answer can tell whether a later switch is still queued behind it. */
const presetWriteKey = (action: PresetAction) => ["printoutPresetWrite", action] as const;

/** Printouts the user has, Printout 1 included — what "preset_count" means in every event. */
const printoutCount = (state: PrintoutPresetsResponse | undefined): number | null => (state ? state.presets.length + 1 : null);

const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/**
 * Whether a switch queued after a write is still waiting. Called from that write's own callbacks, while
 * it still counts as pending — so a switch subtracts itself.
 */
const isLaterSwitchPending = (queryClient: QueryClient, departmentId: string, action: PresetAction): boolean => {
	const scopeId = presetWriteScope(departmentId).id;
	const pendingSwitches = queryClient.isMutating({ mutationKey: presetWriteKey("select"), predicate: (mutation) => mutation.options.scope?.id === scopeId });
	return pendingSwitches > (action === "select" ? 1 : 0);
};

/** Undoes a failed write's optimistic change — only as far as no later switch has replaced it. */
const rollBackOptimistic = <TVariables>(
	queryClient: QueryClient,
	context: PresetMutationContext,
	previous: PrintoutPresetsResponse,
	optimistic: NonNullable<PresetMutationConfig<TVariables>["optimistic"]>,
	variables: TVariables,
	action: PresetAction,
): void => {
	const laterSwitchPending = isLaterSwitchPending(queryClient, context.departmentId, action);
	queryClient.setQueryData<PrintoutPresetsResponse>(context.queryKey, (current) =>
		current ? optimistic.rollback(current, previous, variables, laterSwitchPending) : previous,
	);
};

/**
 * A failed write can mean the cache is out of date (e.g. the preset was removed in another window), so
 * reload the server's list — the not-found message tells the user it was refreshed. Not while later
 * writes are still queued: a reload answered before them would undo their on-screen changes (a later
 * switch), and each of their answers brings the server's state anyway.
 */
const reloadAfterFailedWrite = (queryClient: QueryClient, departmentId: string, queryKey: QueryKey): void => {
	const failedScope = presetWriteScope(departmentId).id;
	// The failed write still counts as pending while its own `onError` runs, hence "more than one".
	const queuedWrites = queryClient.isMutating({ predicate: (mutation) => mutation.options.scope?.id === failedScope });
	if (queuedWrites <= 1) queryClient.invalidateQueries({ queryKey });
};

const presetEventProps = (departmentId: string, presetId: string | null, state: PrintoutPresetsResponse | undefined): AnalyticValues => ({
	department_id: departmentId,
	preset_id: presetId,
	preset_count: printoutCount(state),
});

const usePresetMutation = <TVariables>({
	action,
	request,
	presetIdOf,
	failureMessage,
	trackLifecycle,
	writeAnswer,
	optimistic,
	departmentOf,
}: PresetMutationConfig<TVariables>) => {
	const { departmentId: departmentOnScreen, userInfo } = usePresetScope();
	const queryClient = useQueryClient();
	const { track, trackError } = useAnalytics();
	const showErrorSnackbar = useErrorSnackbar();
	const event = `printout_preset_${action}`;
	const writeDepartment = (variables: TVariables): string => (departmentOf ? departmentOf(variables) : departmentOnScreen);

	return useMutation<PrintoutPresetsResponse, unknown, TVariables, PresetMutationContext>({
		mutationKey: presetWriteKey(action),
		scope: presetWriteScope(departmentOnScreen),
		// Runs as soon as the write is made, even while it waits its turn in the scope — so a switch shows at once.
		onMutate: async (variables) => {
			const departmentId = writeDepartment(variables);
			const queryKey = printoutPresetKeys.department(departmentId, userInfo.id);
			const previous = queryClient.getQueryData<PrintoutPresetsResponse>(queryKey);
			if (trackLifecycle) track(`${event}_start`, presetEventProps(departmentId, presetIdOf(variables, previous), previous));
			if (optimistic && previous) {
				await queryClient.cancelQueries({ queryKey });
				queryClient.setQueryData<PrintoutPresetsResponse>(queryKey, optimistic.apply(previous, variables));
			}
			return { queryKey, departmentId, previous };
		},
		mutationFn: async (variables) => (await request(writeDepartment(variables), variables)).data,
		onSuccess: (data, variables, context) => {
			const laterSwitchPending = isLaterSwitchPending(queryClient, context.departmentId, action);
			queryClient.setQueryData<PrintoutPresetsResponse>(context.queryKey, (current) => writeAnswer(current, data, variables, laterSwitchPending));
			// After an add the new preset only exists in the response — it is the one the server made active.
			const presetId = action === "add" ? data.activePresetId : presetIdOf(variables, data);
			if (trackLifecycle) track(`${event}_success`, presetEventProps(context.departmentId, presetId, data));
		},
		onError: (error, variables, context) => {
			if (optimistic && context?.previous) rollBackOptimistic(queryClient, context, context.previous, optimistic, variables, action);
			// DEFAULT: `context` is missing only when `onMutate` itself threw. The scope, query key and event
			// props then fall back to the write's department — the write was made in it, with no snapshot taken.
			const departmentId = context ? context.departmentId : writeDepartment(variables);
			reloadAfterFailedWrite(queryClient, departmentId, context ? context.queryKey : printoutPresetKeys.department(departmentId, userInfo.id));
			const props = context
				? presetEventProps(context.departmentId, presetIdOf(variables, context.previous), context.previous)
				: presetEventProps(departmentId, null, undefined);
			track(`${event}_error`, props);
			trackError(event, { ...props, error_message: errorMessage(error) });
			showErrorSnackbar(failureMessage(variables), props, error);
		},
	});
};

export type AddPrintoutPresetVariables = { name: string; settings: PresetSettings };

/** Creates a preset; the server makes it the active one. */
export const useAddPrintoutPreset = () => {
	const { t } = useTranslation();
	return usePresetMutation<AddPrintoutPresetVariables>({
		action: "add",
		request: (departmentId, body) => backendService.createPrintoutPreset(departmentId, body),
		presetIdOf: () => null,
		failureMessage: () => t("printout.preset.add.failed"),
		trackLifecycle: true,
		writeAnswer: (current, answer, _variables, laterSwitchPending) => takeAnswerKeepingLaterSwitch(current, answer, laterSwitchPending),
	});
};

/** Deletes a preset by id. Deleting the active preset switches the user back to Printout 1. */
export const useRemovePrintoutPreset = () => {
	const { t } = useTranslation();
	return usePresetMutation<string>({
		action: "remove",
		request: (departmentId, presetId) => backendService.deletePrintoutPreset(departmentId, presetId),
		presetIdOf: (presetId) => presetId,
		failureMessage: () => t("printout.preset.remove.failed"),
		trackLifecycle: true,
		writeAnswer: (current, answer, _variables, laterSwitchPending) => takeAnswerKeepingLaterSwitch(current, answer, laterSwitchPending),
	});
};

export type RenamePrintoutPresetVariables = { presetId: string; name: string };

export const useRenamePrintoutPreset = () => {
	const { t } = useTranslation();
	return usePresetMutation<RenamePrintoutPresetVariables>({
		action: "rename",
		request: (departmentId, { presetId, name }) => backendService.renamePrintoutPreset(departmentId, presetId, name),
		presetIdOf: ({ presetId }) => presetId,
		failureMessage: () => t("printout.preset.rename.failed"),
		trackLifecycle: true,
		writeAnswer: (current, answer, { presetId }) => withAnsweredPreset(current, answer, presetId),
	});
};

/**
 * Switches the printout in use; `null` is Printout 1. Applied to the cache at once so the tabs switch
 * without waiting for the server, and switched back if the request fails — unless the user has
 * switched again since, in which case their later choice stands.
 */
export const useSelectPrintoutPreset = () => {
	const { t } = useTranslation();
	return usePresetMutation<string | null>({
		action: "select",
		request: (departmentId, presetId) => backendService.setActivePrintoutPreset(departmentId, presetId),
		presetIdOf: (presetId) => presetId,
		failureMessage: () => t("printout.preset.select.failed"),
		trackLifecycle: true,
		writeAnswer: (current, answer, _variables, laterSwitchPending) => takeAnswerKeepingLaterSwitch(current, answer, laterSwitchPending),
		optimistic: {
			apply: (current, presetId) => ({ ...current, activePresetId: presetId }),
			// Only while the cache still shows THIS switch and no later one is queued — never over a later choice.
			rollback: (current, previous, presetId, laterSwitchPending) =>
				current.activePresetId === presetId && !laterSwitchPending ? { ...current, activePresetId: previous.activePresetId } : current,
		},
	});
};

export type SavePrintoutPresetSettingsVariables = {
	/** Where the edit was made — the save goes there even if the user has switched departments since. */
	departmentId: string;
	presetId: string;
	/** The preset's display name, used only in the failure message. */
	presetName: string;
	settings: PresetSettings;
};

/**
 * Saves a preset's settings (the printout panel's autosave). Only failures are tracked: the panel
 * saves after every pause in editing, so start/success events would be noise, not signal.
 */
export const useSavePrintoutPresetSettings = () => {
	const { t } = useTranslation();
	return usePresetMutation<SavePrintoutPresetSettingsVariables>({
		action: "save",
		request: (departmentId, { presetId, settings }) => backendService.updatePrintoutPresetSettings(departmentId, presetId, settings),
		departmentOf: ({ departmentId }) => departmentId,
		presetIdOf: ({ presetId }) => presetId,
		failureMessage: ({ presetName }) => t("printout.preset.save.failed", { name: presetName }),
		trackLifecycle: false,
		writeAnswer: (current, answer, { presetId }) => withAnsweredPreset(current, answer, presetId),
	});
};

/**
 * After the department's printouts replaced this user's (Save as default, or Use department printouts):
 * shows Printout 1 at once — the server has already put them there — then reloads the list, whose saved
 * printouts may all be different now. Resolves once the reload has answered.
 */
export const useReloadPrintoutPresetsOnPrintout1 = () => {
	const { departmentId, userInfo } = usePresetScope();
	const queryClient = useQueryClient();
	const userId = userInfo.id;
	return useCallback(async (): Promise<void> => {
		const queryKey = printoutPresetKeys.department(departmentId, userId);
		queryClient.setQueryData<PrintoutPresetsResponse>(queryKey, (current) => (current ? { ...current, activePresetId: null } : current));
		await queryClient.invalidateQueries({ queryKey });
	}, [queryClient, departmentId, userId]);
};

/**
 * Reads the saved printouts again without letting an older answer undo a write: the read is skipped
 * while any preset write for the department is in flight, and its answer is thrown away — never written
 * to the cache — if a write started or finished while it was on its way (that write's own answer is
 * newer). Resolves the state it wrote, or `null` when it skipped or discarded the read. A failed read
 * rejects and writes nothing; the cached list stays.
 */
export const useRefreshPrintoutPresets = () => {
	const { departmentId, userInfo } = usePresetScope();
	const queryClient = useQueryClient();
	const userId = userInfo.id;
	return useCallback(async (): Promise<PrintoutPresetsResponse | null> => {
		const scopeId = presetWriteScope(departmentId).id;
		const isPresetWrite = (mutation: { options: { scope?: { id: string } } }): boolean => mutation.options.scope?.id === scopeId;
		if (queryClient.isMutating({ predicate: isPresetWrite }) > 0) return null;
		let writeSeen = false;
		const stopWatching = queryClient.getMutationCache().subscribe((event) => {
			if (event.mutation && isPresetWrite(event.mutation)) writeSeen = true;
		});
		try {
			const answer = (await backendService.getPrintoutPresets(departmentId)).data;
			if (writeSeen) return null;
			queryClient.setQueryData<PrintoutPresetsResponse>(printoutPresetKeys.department(departmentId, userId), answer);
			return answer;
		} finally {
			stopWatching();
		}
	}, [queryClient, departmentId, userId]);
};

/**
 * How many people in the department have saved printouts of their own — what Save as default with saved
 * printouts would delete. Support only (the endpoint refuses anyone else), so it loads only while
 * `enabled` (the confirm dialog is open), and freshly each time it opens.
 */
export const usePrintoutPresetsDefaultImpact = (enabled: boolean) => {
	const { departmentId } = usePresetScope();
	return useQuery<{ usersWithOwnPresets: number }>({
		queryKey: printoutPresetKeys.defaultImpact(departmentId),
		queryFn: async () => (await backendService.getPrintoutPresetsDefaultImpact(departmentId)).data,
		enabled: enabled && !!departmentId,
		// A count from an earlier opening could be out of date by now: ask again every time.
		staleTime: 0,
		gcTime: 0,
		refetchOnWindowFocus: false,
	});
};
